import { useEffect } from 'react';
import { t } from '@lingui/macro';

import Loading from '@decisiv/ui-components/lib/components/Loading';

import { usePollAssetIdentificationBatch } from '../StepTwo/usePollAssetIdentificationBatch';
import { useAddAssetContext } from '../context/AddAssetContext';
import { type ProposedIdentificationBatch } from '../types';

/**
 * Intermediate ("between steps") content shown while VINs are validated, after
 * the user leaves Select and before Review is revealed. It polls the
 * identification batch, writes the categorized results into saved state, then
 * advances to Review. On failure it advances to Review flagged so the step can
 * render its error.
 */
export default function IdentifyingAssets() {
  const {
    savedState,
    setSavedState,
    setIntermediate,
    forward,
    setStepTwoContinueButtonEnabled,
  } = useAddAssetContext();

  const { startPolling, data, pollingFinished, error } =
    usePollAssetIdentificationBatch();

  const { batchId } = savedState.stepTwo;

  useEffect(() => {
    if (batchId) {
      startPolling(batchId);
    }
  }, [batchId, startPolling]);

  useEffect(() => {
    if (!pollingFinished) return;

    // The identification batch carries the proposed PD-3375 shape (invalid
    // reasons + other-depot / current buckets). Cast until the generated types
    // catch up.
    const batch = data?.assetIdentificationBatch as unknown as
      | ProposedIdentificationBatch
      | undefined;

    // Failure: land on Review flagged so it shows the error alert.
    if (!batchId || !batch || batch.status === 'failed' || error) {
      setSavedState((prev) => ({
        ...prev,
        stepTwo: { ...prev.stepTwo, failed: true },
      }));
      setStepTwoContinueButtonEnabled(false);
      setIntermediate(false);
      forward();
      return;
    }

    const validVins = batch.validAssets ?? [];
    const usedByOther = batch.ownedByOtherFleetAssets ?? [];
    const usedByOtherDepot = batch.ownedByOtherDepotAssets ?? [];
    const ownedByCurrent = batch.ownedByCurrentAssets ?? [];
    const invalidVins = batch.invalidAssets ?? [];

    setSavedState((prev) => ({
      ...prev,
      stepTwo: {
        ...prev.stepTwo,
        failed: false,
        validVins,
        invalidVins,
        usedByOther,
        usedByOtherDepot,
        ownedByCurrent,
      },
    }));

    // The Review step only presents new, re-homed, and invalid VINs. Assets
    // already in the target depot are handled solely on Add Unit #.
    const reviewCount =
      validVins.length +
      usedByOther.length +
      usedByOtherDepot.length +
      invalidVins.length;

    // Any asset that can be added or edited (new, other fleet, other depot, or
    // already in this depot) lets the user proceed. Invalid VINs are skipped.
    setStepTwoContinueButtonEnabled(reviewCount + ownedByCurrent.length > 0);
    setIntermediate(false);

    // Nothing to review (every entered VIN is already in this depot) — skip the
    // empty Review step and go straight to Add Unit #.
    if (reviewCount === 0 && ownedByCurrent.length > 0) {
      forward();
      forward();
    } else {
      forward();
    }
  }, [
    pollingFinished,
    data,
    batchId,
    error,
    setSavedState,
    setIntermediate,
    forward,
    setStepTwoContinueButtonEnabled,
  ]);

  return (
    <Loading
      size="small"
      title={t`Validating VIN(s)…`}
      description={t`Your assets are being processed. This might take a while.`}
    />
  );
}
